import React from 'react';
import { Helmet } from 'react-helmet';

// Helper functions to truncate text and keywords
const truncateText = (text, maxLength) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

const getOptimizedKeywords = (keywords, maxKeywords) => {
  if (!keywords) return "";
  return keywords.slice(0, maxKeywords).join(', ');
};

const ProductSEO = ({ product }) => {
  const productURL = typeof window !== 'undefined' ? window.location.href : '';

  // 1. Logic for "Shop Now" visibility & Dynamic Expiry
  const today = new Date();
  const nextYear = new Date(today.setFullYear(today.getFullYear() + 1));
  const priceValidUntil = nextYear.toISOString().split('T')[0];

  // 2. Fallback Price logic
  const displayPrice = (product.price && parseFloat(product.price) > 0) 
    ? product.price 
    : "999.99";

  const maxTitleLength = 60; 
  const maxDescriptionLength = 160; 
  const maxKeywords = 10;

  const title = truncateText(product.name, maxTitleLength);
  const description = truncateText(product.description, maxDescriptionLength);
  const optimizedKeywords = getOptimizedKeywords(product.keywords, maxKeywords);
  
  const imageUrl = product.imageUrls && product.imageUrls.length > 0 
    ? product.imageUrls[0] 
    : (product.image || 'https://emg.trustglobal.eu.org/images/no-p.jpg');

  const schemaMarkup = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": title,
    "image": [imageUrl],
    "description": description,
    "sku": product.sku || product.id || "N/A",
    "brand": {
      "@type": "Brand",
      "name": product.brand || "Trust Global"
    },
    "offers": {
      "@type": "Offer",
      "url": productURL,
      "priceCurrency": "USD",
      "price": displayPrice,
      "priceValidUntil": priceValidUntil,
      "itemCondition": "https://schema.org/NewCondition",
      "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0.00",
          "currency": "USD"
        }
      }
    }
  };

  return (
    <Helmet>
      {/* Title Tag */}
      <title>{`${title} - Trust Global`}</title>

      {/* Meta Description */}
      <meta name="description" content={description} />

      {/* Keywords */}
      <meta name="keywords" content={optimizedKeywords} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={`${title} - Our Store`} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="product" />
      <meta property="og:url" content={productURL} />
      <meta property="og:image" content={imageUrl} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${title} - Our Store`} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Favicon */}
      <link rel="icon" href="https://www.trustglobal.com.bd/favicon.ico" type="image/x-icon" />

      {/* Canonical URL */}
      <link rel="canonical" href={productURL} />
      
      {/* Google Analytics */}
      <script async src="https://www.googletagmanager.com/gtag/js?id=G-CHPRRG086V"></script>
      <script>
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag() {
            dataLayer.push(arguments);
          }
          gtag('js', new Date());
          gtag('config', 'G-CHPRRG086V');
        `}
      </script>

      {/* Microsoft Clarity */}
      <script>
        {`
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i+"?ref=bwt";
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "nhegnrn92a");
        `}
      </script>

      {/* Structured Data for Rich Snippets */}
      <script type="application/ld+json">
        {JSON.stringify(schemaMarkup)}
      </script>
    </Helmet>
  );
};

export default ProductSEO;